import React, { useEffect, useState, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useTranslations } from "../../translations";
import { useSchoolRole } from "../../hooks/useSchoolRole";
import { summarizeEvents } from "../../components/protected/activity/summarizeEvents";
import { StudentActivityModal } from "../../components/protected/activity/StudentActivity";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const ACTIVE_DAYS = 7; // mismo criterio que el panel del representante
const daysAgo = (ms) => Math.floor((Date.now() - ms) / 864e5);
const fmtMinutes = (m) => (m < 1 ? "—" : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`);

/** Actividad de todos los alumnos que ve el usuario (escuela, profesor o representante). */
const ClassActivity = () => {
  const { t } = useTranslations();
  const { role, loading: roleLoading } = useSchoolRole();
  const allowed = ["escuela", "maestro", "representante"].includes(role);
  const [data, setData] = useState(null); // null = cargando
  const [error, setError] = useState(null);
  const [section, setSection] = useState("");
  const [q, setQ] = useState("");
  const [actStu, setActStu] = useState(null);

  useEffect(() => {
    if (roleLoading || !allowed) return;
    let active = true;
    (async () => {
      try {
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const res = await fetch(`${API_BASE}/api/school/class-activity`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || "Error");
        if (active) setData(json);
      } catch (e) {
        if (active) setError(e.message);
      }
    })();
    return () => { active = false; };
  }, [roleLoading, allowed]);

  // Los que no practican van primero: ordena por última actividad, "nunca" arriba.
  const rows = useMemo(
    () =>
      (data?.students || [])
        .map((s) => ({ ...s, sum: summarizeEvents(s.events) }))
        .sort((a, b) => (a.lastActivityMs || 0) - (b.lastActivityMs || 0)),
    [data]
  );
  const term = q.trim().toLowerCase();
  const shown = rows.filter((s) => (!section || s.sectionId === section) && (!term || String(s.learnerName || "").toLowerCase().includes(term)));
  const activeCount = shown.filter((s) => s.sum.activeDays > 0).length;

  const status = (ms) => {
    if (!ms) return <span className="ca-chip ca-never">{t("stNever")}</span>;
    const d = daysAgo(ms);
    if (d <= ACTIVE_DAYS) return <span className="ca-chip ca-active">{t("stActive")} · {new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>;
    return <span className="ca-chip ca-inactive">{t("stInactive")} · {d}{t("daysShort")}</span>;
  };

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto" style={{ maxWidth: 1100 }}>
          <h2 className="fw-bold color-plum mb-1">📈 {t("clsTitle")}</h2>
          <p className="text-muted mb-3">{t("clsDesc")}</p>

          {roleLoading || (allowed && !data && !error) ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : !allowed ? (
            <div className="modern-card p-4 text-center text-muted">{t("notAuthorized")}</div>
          ) : error ? (
            <div className="modern-card p-4 text-danger">{error}</div>
          ) : (
            <div className="modern-card p-3 p-md-4">
              <div className="ca-tools">
                <Form.Control className="ca-search" placeholder={t("searchStudent")} value={q} onChange={(e) => setQ(e.target.value)} />
                {data.sections.length > 1 && (
                  <Form.Select className="ca-select" value={section} onChange={(e) => setSection(e.target.value)}>
                    <option value="">{t("clsAllSections")}</option>
                    {data.sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </Form.Select>
                )}
                <span className="ca-count">{t("clsActiveWeek").replace("{n}", activeCount).replace("{total}", shown.length)}</span>
              </div>

              {shown.length === 0 ? (
                <div className="text-center text-muted py-4">{t("noStudents")}</div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="ca-table mb-0">
                    <thead><tr>
                      <th>{t("studentName")}</th>
                      <th>{t("section")}</th>
                      <th>{t("actLast")}</th>
                      <th className="text-center">{t("actActiveDays")}<div className="ca-sub">{t("clsPeriod")}</div></th>
                      <th className="text-center">{t("actTime")}<div className="ca-sub">{t("clsPeriod")}</div></th>
                      <th className="text-center">{t("actStars")}<div className="ca-sub">{t("clsPeriod")}</div></th>
                    </tr></thead>
                    <tbody>
                      {shown.map((s) => (
                        <tr key={s.uid} className="ca-row" onClick={() => setActStu(s)} title={t("actView")}>
                          <td className="fw-semibold">{s.learnerName || "—"}</td>
                          <td>{[s.grade, s.section].filter(Boolean).join(" · ") || "—"}</td>
                          <td>{status(s.lastActivityMs)}</td>
                          <td className="text-center">{s.sum.activeDays || "—"}</td>
                          <td className="text-center">{fmtMinutes(s.sum.minutes)}</td>
                          <td className="text-center">{s.sum.stars ? `⭐ ${s.sum.stars}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </Container>
        <StudentActivityModal student={actStu} onHide={() => setActStu(null)} />
        <Footer logged={true} />
      </main>
      <style>{`
        .ca-tools { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin-bottom:12px; }
        .ca-search { max-width:260px; }
        .ca-select { max-width:220px; }
        .ca-count { margin-left:auto; color:#666; font-size:.9rem; }
        .ca-table th { font-size:.85rem; white-space:nowrap; }
        .ca-sub { font-weight:400; font-size:.7rem; color:#999; }
        .ca-row { cursor:pointer; }
        .ca-chip { display:inline-block; padding:2px 10px; border-radius:999px; font-size:.8rem; white-space:nowrap; }
        .ca-active { background:#e3f7ec; color:#1e7d4a; }
        .ca-inactive { background:#fff4e0; color:#a5620a; }
        .ca-never { background:#fde8ea; color:#b42335; }
      `}</style>
    </div>
  );
};

export default ClassActivity;
